import { type FunctionComponent, type HTMLAttributes } from 'react';
import { PLACEHOLDER_OPTIONS } from '../../../../constants/cloudinary/general';
import { type CloudinaryOptions } from '../../../../helpers/cloudinary-helper/cloudinary-helper';
import { type ShopByLookSlide as SlideItem } from '../../../../types/home.types';
import { Link } from '../../../common-components/link/link.component';
import { CloudinaryImage } from '../../../images/cloudinary-image/cloudinary-image.component';

export type ShopByLookTypeSlideProps = {
	item: SlideItem;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ShopByLookTextSlide: FunctionComponent<ShopByLookTypeSlideProps> = ({ className = 'lh-copy pa4', item }) => {
	const { title, description } = item;
	return (
		<div className={`flex flex-column justify-around bg-theme-white w-100 h-100 ${className}`} data-testid="textSlide">
			<h4 className="tl ma0 mb4 f4">{title}</h4>
			<p className="tl mv2">{description}</p>
		</div>
	);
};

export const ShopByLookImageSlide: FunctionComponent<ShopByLookTypeSlideProps> = ({
	className = 'flex flex-row items-end w-100',
	item
}) => {
	const { image } = item;
	const options: CloudinaryOptions = {
		type: image.imageType,
		format: 'jpg',
		width: undefined,
		height: 715,
		quality: 80
	};
	return (
		<div className={className} data-testid="imageSlide">
			<CloudinaryImage
				className="relative w-100 w-auto-l mw-none-l"
				description={image.description}
				publicID={image.id}
				options={options}
				useDimensions={false}
				placeholderOptions={{
					...options,
					...PLACEHOLDER_OPTIONS
				}}
				lowQualityPlaceholder={image.lowQualityPlaceholder}
			/>
		</div>
	);
};

export type ShopByLookSlideProps = {
	slideContentClass?: string;
	SlideContent?: FunctionComponent<ShopByLookTypeSlideProps>;
} & ShopByLookTypeSlideProps;

export const ShopByLookSlide: FunctionComponent<ShopByLookSlideProps> = ({
	className = 'w-100',
	slideContentClass,
	SlideContent = ShopByLookTextSlide,
	item
}) => {
	const { title, trackingLink } = item;
	const { url, tracking } = trackingLink;
	return (
		<Link
			className={`${className} overflow-hidden pointer`}
			color="grey-darker"
			url={url}
			ariaLabel={`Shop By Look - ${title}`}
			analyticsHook={tracking}>
			<SlideContent item={item} className={slideContentClass} />
		</Link>
	);
};
